import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import { ddLogger } from '@b-stock/fe-core/bstock-react'
import { FeeType } from '@b-stock/shipment-api-client'

// Cap on the dedupe set so a long SPA session with a degraded backend
// can't grow this unboundedly. If a listing is evicted and comes back
// malformed, we'll log it once more — acceptable at this scale.
export const MAX_LOGGED_MALFORMED_LISTINGS = 100
export const loggedMalformedFeesForListing = new Set<string>()

export const calculateShippingCostBreakdown = (
  selectedQuote?: OrchestratedQuote | null
): {
  estimatedShippingAmount: number
  liftgateDeliveryFee?: number
  residentialDeliveryFee?: number
} => {
  const rawFees = selectedQuote?.fees
  const fees = Array.isArray(rawFees) ? rawFees : []

  if (selectedQuote && !Array.isArray(rawFees)) {
    const { listingId } = selectedQuote
    if (!loggedMalformedFeesForListing.has(listingId)) {
      loggedMalformedFeesForListing.add(listingId)
      if (loggedMalformedFeesForListing.size > MAX_LOGGED_MALFORMED_LISTINGS) {
        const oldest = loggedMalformedFeesForListing.values().next().value
        if (oldest !== undefined) {
          loggedMalformedFeesForListing.delete(oldest)
        }
      }
      ddLogger.error(
        'calculateShippingCostBreakdown received malformed quote.fees',
        {
          listingId,
          hasSelectedQuote: true,
          feesType: typeof rawFees,
          feesIsArray: false,
        }
      )
    }
  }

  const liftgateDeliveryFee = fees.find(
    ({ type }) => type === FeeType.DestinationLiftgate
  )?.value
  const residentialDeliveryFee = fees.find(
    ({ type }) => type === FeeType.DestinationResidential
  )?.value

  // At request of product (Yang), 2025-09-25, we no longer want to break out
  // any of the fees other than the two above (liftgate and residential), it
  // will all be lumped into the base fee (ticket: ZRO-5039)
  const estimatedShippingAmount =
    (selectedQuote?.totalPrice || 0) -
    (residentialDeliveryFee || 0) -
    (liftgateDeliveryFee || 0)

  return {
    estimatedShippingAmount,
    liftgateDeliveryFee,
    residentialDeliveryFee,
  }
}
